/**
 * Shared browser-cookie helpers (SSoT).
 *
 * Extracted from the referral-cookie read/write logic that used to be
 * duplicated across routes.js, register.vue, referral-landing.vue and
 * authApi.js — the promo flow rides the same rails, so the parsing and
 * serialization live here once.
 *
 * The pure core (readCookieValue / buildCookieString) never touches
 * `document`, so it runs under plain Node in scripts/promo-logic.test.mjs.
 */

export const REFERRAL_COOKIE = 'affiloop_referral';
export const PROMO_COOKIE = 'affiloop_promo';

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Extract a cookie value from a raw cookie string (document.cookie shape).
 * @param {string} cookieString e.g. "a=1; affiloop_promo=WELCOME10"
 * @param {string} name cookie name
 * @returns {string|null}
 */
export function readCookieValue(cookieString, name) {
  const match = String(cookieString || '').match(
    new RegExp(`(?:^|;\\s*)${escapeRegExp(name)}=([^;]+)`)
  );
  return match ? match[1] : null;
}

/**
 * Build the document.cookie assignment string for a cookie that expires in
 * `days` days. Attributes match the original referral cookie exactly
 * (path=/; SameSite=Lax).
 * @param {string} name
 * @param {string} value
 * @param {number} days
 * @param {number} [now] epoch ms, injectable for tests
 * @returns {string}
 */
export function buildCookieString(name, value, days, now = Date.now()) {
  const expires = new Date(now + days * 864e5).toUTCString();
  return `${name}=${value}; expires=${expires}; path=/; SameSite=Lax`;
}

/** Read a cookie from the live document. */
export function getCookie(name) {
  return readCookieValue(document.cookie, name);
}

/** Write a cookie on the live document, expiring in `days` days. */
export function setCookie(name, value, days) {
  document.cookie = buildCookieString(name, value, days);
}

/** Delete a cookie on the live document (expiry in the past). */
export function clearCookie(name) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
}
